// Takeoff Canvas 2.0 Track B — scale verification (TAKEOFF_CANVAS_2_ROADMAP.md
// Track B, code examples §24, compatibility audit §9). Pure functions.
//
// Per-sheet scale already lives on TakeoffDrawingSheets (ImageFtPerInch for a
// PDF, ImageScaleFtPerPx for a raster) — there is no second scale store.
// "Verify scale" used to re-calibrate the moment a distance was typed. Now
// the two clicked points are MEASURED at the current scale first and compared
// with the dimension printed on the drawing: an error percentage and a
// verdict, and only an explicit "re-calibrate" writes anything. The verdict
// thresholds are the brief's (§24): ≤ 2 % good, ≤ 5 % review, else fail.

export const SCALE_VERDICT_THRESHOLDS = Object.freeze({ goodPct: 2, reviewPct: 5 });

export const SCALE_VERDICTS = Object.freeze({
  good:    { key: "good",    label: "Scale verified",        tone: "ok" },
  review:  { key: "review",  label: "Scale off — review",    tone: "warn" },
  fail:    { key: "fail",    label: "Scale wrong",           tone: "bad" },
  invalid: { key: "invalid", label: "Enter the printed dimension", tone: "muted" },
});

// measuredFt = the two clicked points' distance at the CURRENT calibration;
// expectedFt = the dimension printed on the drawing between those points.
export function verifyScale({ measuredFt, expectedFt, thresholds = SCALE_VERDICT_THRESHOLDS }) {
  const m = +measuredFt, e = +expectedFt;
  if (!(m > 0) || !(e > 0) || !Number.isFinite(m) || !Number.isFinite(e)) return { verdict: "invalid", errorPct: null, ratio: null, measuredFt: m > 0 ? m : null, expectedFt: e > 0 ? e : null };
  const errorPct = Math.abs(m - e) / e * 100;
  const verdict = errorPct <= thresholds.goodPct ? "good" : errorPct <= thresholds.reviewPct ? "review" : "fail";
  return { verdict, errorPct, ratio: e / m, measuredFt: m, expectedFt: e, deltaFt: m - e };
}

// What the sheet's calibration would become if re-calibrated to the printed
// dimension — the same arithmetic the canvas used to apply silently.
//   pdf:    worldDist = paperInch × ftPerInch  →  newFtPerInch = distFt ÷ paperInch
//   raster: worldDist = distPx × ftPerPx        →  newFtPerPx  = distFt ÷ distPx
export function proposedCalibration({ worldDist, expectedFt, isPdf, ftPerInch, ftPerPx }) {
  const w = +worldDist, e = +expectedFt;
  if (!(w > 0) || !(e > 0)) return null;
  if (isPdf) {
    if (!(ftPerInch > 0)) return null;
    const paperInch = w / ftPerInch;
    const next = e / paperInch;
    return { kind: "pdf", key: "image_ft_per_inch", value: next, current: ftPerInch, changePct: (next - ftPerInch) / ftPerInch * 100 };
  }
  if (!(ftPerPx > 0)) return null;
  const distPx = w / ftPerPx;
  const next = e / distPx;
  return { kind: "raster", key: "image_scale_ft_per_px", value: next, current: ftPerPx, changePct: (next - ftPerPx) / ftPerPx * 100 };
}

export const fmtPct = (v) => (v == null || !Number.isFinite(v) ? "—" : `${Math.round(v * 10) / 10}%`);
